import { useCallback, useMemo, useState } from 'react';
import { difference } from 'es-toolkit';

import { type QuizGenerateItem } from '@/api/ai/type';

import { MAX_QUIZ_COUNT } from '../constant';

import { useBuildQuestionAlert } from './useBuildQuestionAlert';

export const useAiGeneratedQuizItem = (
  generatedItems: QuizGenerateItem[],
  options: { countLimit: number },
) => {
  const [selectedItems, setSelectedItems] = useState<QuizGenerateItem[]>([]);
  const [savedItems, setSavedItems] = useState<QuizGenerateItem[]>([]);

  const unSavedItems = useMemo(() => {
    return difference(generatedItems, savedItems);
  }, [generatedItems, savedItems]);

  const { countLimit } = options;

  // 남은 저장 가능한 개수
  const remainingSaveQuota = useMemo(() => {
    return countLimit - savedItems.length;
  }, [countLimit, savedItems]);

  const { openQuestionCountLimitDetailedAlert } = useBuildQuestionAlert();

  const handleSelectItem = useCallback(
    (item: QuizGenerateItem) => {
      const isSelected = selectedItems.includes(item);

      if (isSelected) {
        setSelectedItems(selectedItems.filter(d => d !== item));
        return;
      }

      if (selectedItems.length >= remainingSaveQuota) {
        openQuestionCountLimitDetailedAlert(MAX_QUIZ_COUNT, remainingSaveQuota);
        return;
      }
      setSelectedItems([...selectedItems, item]);
    },
    [selectedItems, remainingSaveQuota, openQuestionCountLimitDetailedAlert],
  );

  const handleSelectItemsAtOnce = useCallback(
    (items: QuizGenerateItem[]) => {
      const notSavedItems = items.filter(item => !savedItems.includes(item));

      setSelectedItems([...notSavedItems.slice(0, remainingSaveQuota)]);
    },
    [savedItems, remainingSaveQuota],
  );

  const handleSaveItems = useCallback(
    (items: QuizGenerateItem[]) => {
      if (items.length > remainingSaveQuota) return;

      setSavedItems([...savedItems, ...items]);
      setSelectedItems([]);
    },
    [savedItems, remainingSaveQuota],
  );

  const handleDeleteItem = useCallback((item: QuizGenerateItem) => {
    setSelectedItems(prev => prev.filter(d => d !== item));
  }, []);

  const reset = useCallback(() => {
    setSelectedItems([]);
    setSavedItems([]);
  }, []);

  return {
    selectedItems,
    savedItems,
    unSavedItems,
    setSelectedItems,
    handleSelectItem,
    handleSelectItemsAtOnce,
    handleSaveItems,
    handleDeleteItem,
    reset,
  };
};
